import axios from 'axios'
import { z } from 'zod'
import { convertFileToBase64, parseRequestError } from '@/common/utilities.ts'
import {
  CreateProviderAffiliation,
  DeleteProviderAffiliationResponseSchema,
  ProviderAffiliationSchema,
  UpdateProviderAffiliation,
  PaginatedAffiliationProvidersSchema,
  RemoveProviderFromAffiliationResponseSchema,
  AddProvidersToAffiliationResponseSchema,
} from '@/queries/providers/affiliations/schema.ts'
import { instanceOfError, RequestError } from '@/common/types_and_interfaces.ts'

export async function GETProviderAffiliations() {
  try {
    const response = await axios.get('/affiliations')
    return z.array(ProviderAffiliationSchema).parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

type ProviderAffiliationRequestPayload = {
  name?: string
  description?: string | null
  status?: 0 | 1
  logo_image?: string | null
}

export async function POSTProviderAffiliation(data: CreateProviderAffiliation) {
  try {
    const payload: ProviderAffiliationRequestPayload = {
      name: data.name,
      description: data.description,
      status: data.status,
      logo_image:
        data.logo_image instanceof File
          ? await convertFileToBase64(data.logo_image)
          : (data.logo_image ?? null),
    }

    const response = await axios.post('/affiliations', payload)
    return ProviderAffiliationSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function PUTProviderAffiliation(
  id: number,
  data: UpdateProviderAffiliation
) {
  try {
    const payload: ProviderAffiliationRequestPayload = {
      name: data.name,
      description: data.description,
      status: data.status,
      ...(data.logo_image !== undefined && {
        logo_image:
          data.logo_image instanceof File
            ? await convertFileToBase64(data.logo_image)
            : data.logo_image,
      }),
    }

    const response = await axios.put(`/affiliations/${id}`, payload)
    return ProviderAffiliationSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function DELETEProviderAffiliation(id: number) {
  try {
    const response = await axios.delete(`/affiliations/${id}`)
    return DeleteProviderAffiliationResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function GETProviderAffiliationById(id: number) {
  try {
    const response = await axios.get(`/affiliations/${id}`)
    return ProviderAffiliationSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function GETProvidersForAffiliation(params: {
  affiliationId: number
  programId: number
  currentPage: number
  recordsPerPage: number
  search?: string
}) {
  try {
    const { affiliationId, programId, currentPage, recordsPerPage, search } =
      params
    const response = await axios.get(
      `admin/affiliations/${affiliationId}/providers`,
      {
        params: {
          program_id: programId,
          page: currentPage,
          per_page: recordsPerPage,
          ...(search ? { search } : {}),
        },
      }
    )
    return PaginatedAffiliationProvidersSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function DELETEProviderFromAffiliation(params: {
  affiliationId: number
  providerId: number
  programId: number
}) {
  try {
    const { affiliationId, providerId, programId } = params
    const response = await axios.delete(
      `admin/affiliations/${affiliationId}/providers/${providerId}`,
      {
        params: {
          program_id: programId,
        },
      }
    )
    return RemoveProviderFromAffiliationResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function GETAvailableProvidersForAffiliation(params: {
  affiliationId: number
  programId: number
  currentPage: number
  recordsPerPage: number
  search?: string
}) {
  try {
    const { affiliationId, programId, currentPage, recordsPerPage, search } =
      params

    const response = await axios.get(
      `/affiliations/${affiliationId}/available-providers`,
      {
        params: {
          program_id: programId,
          page: currentPage,
          per_page: recordsPerPage,
          ...(search ? { search } : {}),
        },
      }
    )

    return PaginatedAffiliationProvidersSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export async function POSTAddProvidersToAffiliation(params: {
  affiliationId: number
  programId: number
  providerIds: number[]
}) {
  try {
    const { affiliationId, programId, providerIds } = params
    const response = await axios.post(
      `admin/affiliations/${affiliationId}/providers`,
      {
        program_id: programId,
        provider_ids: providerIds,
      }
    )
    return AddProvidersToAffiliationResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

async function parseBlobDownloadError(error: unknown): Promise<RequestError> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const blobText = await error.response.data.text()
      const parsedData = JSON.parse(blobText)

      if (instanceOfError(parsedData)) {
        return parsedData.error
      }

      if (parsedData?.error || parsedData?.message) {
        return {
          code: error.response.status ?? -1,
          message: parsedData.error ?? parsedData.message ?? '',
          details: null,
        }
      }
    } catch {
      // fall back to the shared parser if the blob is not valid JSON.
    }
  }

  return parseRequestError(error)
}

export async function DOWNLOADAffiliationProvidersImportTemplate() {
  try {
    return await axios.get('admin/affiliations/providers/import-template', {
      responseType: 'blob',
    })
  } catch (error) {
    throw await parseBlobDownloadError(error)
  }
}

export async function POSTImportProvidersToAffiliation(params: {
  affiliationId: number
  programId: number
  file: File
}) {
  try {
    const { affiliationId, programId, file } = params
    const formData = new FormData()

    formData.append('program_id', programId.toString())
    formData.append('file', file)

    const response = await axios.post(
      `admin/affiliations/${affiliationId}/providers/import`,
      formData
    )
    return AddProvidersToAffiliationResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}
